import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, signal, viewChild } from '@angular/core';
import pluginDataLabels from 'chartjs-plugin-datalabels';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { SuperEjecutivoDescargaComponent } from '../super-ejecutivo-descarga/super-ejecutivo-descarga.component';
import { SuperEjecutivoDescargaService } from '../super-ejecutivo-descarga/super-ejecutivo-descarga.service';
import {
  descargarExcelSuperEjecutivo,
  descargarImagenSuperEjecutivo,
  nombreArchivoGraficaSuperEjecutivo,
} from '../super-ejecutivo-descarga/super-ejecutivo-descarga.util';
import { EXCEL_SUPER_EJECUTIVO, TEXTOS_DESCARGA_SUPER_EJECUTIVO } from '../super-ejecutivo.constants';
import { ICeldaMapaCalorSuperEjecutivo, IGraficaSuperEjecutivo } from '../super-ejecutivo.interface';
import { tablaExcelGraficaSuperEjecutivo } from './super-ejecutivo-grafica-descarga.util';
import {
  altoGraficaSuperEjecutivo,
  colorIntensidadSuperEjecutivo,
  configuracionChartSuperEjecutivo,
  esGraficaDeLienzoSuperEjecutivo,
  formatearCifraSuperEjecutivo,
  prepararGraficaSuperEjecutivo,
} from './super-ejecutivo-grafica.util';

/**
 * Dibuja una gráfica del Super Ejecutivo (requerimiento `super-ejecutivo-graficas`, RN1, RN2, RN8). Las de
 * categorías y la dispersión van en un lienzo de chart.js con la cifra en cada valor o en la leyenda; el mapa
 * de calor, el progreso y las cifras son HTML. El título siempre va arriba. Cada gráfica se puede descargar como
 * imagen o como Excel sin avisarle al agente; mientras se arma el archivo se muestra la barra de carga del layout y,
 * si falla, un aviso.
 */
@Component({
  selector: 'app-super-ejecutivo-grafica',
  templateUrl: './super-ejecutivo-grafica.component.html',
  styleUrl: './super-ejecutivo-grafica.component.css',
  imports: [BaseChartDirective, SuperEjecutivoDescargaComponent],
  providers: [provideCharts(withDefaultRegisterables())],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperEjecutivoGraficaComponent {
  /** La gráfica válida del contrato, tal como la escribió el Super Ejecutivo. */
  public readonly grafica = input.required<IGraficaSuperEjecutivo>();

  /** La gráfica como se dibuja: ordenada y con "Otros" si hace falta. */
  public readonly vista = computed(() => prepararGraficaSuperEjecutivo(this.grafica()));
  public readonly chart = computed(() => {
    const vista = this.vista();

    return esGraficaDeLienzoSuperEjecutivo(vista) ? configuracionChartSuperEjecutivo(vista) : null;
  });
  public readonly alto = computed(() => altoGraficaSuperEjecutivo(this.vista()));
  public readonly plugins = [pluginDataLabels];

  public readonly etiquetaDescarga = TEXTOS_DESCARGA_SUPER_EJECUTIVO.BOTON_GRAFICA;
  private readonly descargas = inject(SuperEjecutivoDescargaService);
  /** Hay una descarga en curso: el botón se deshabilita para no armar el mismo archivo dos veces. */
  public readonly descargando = signal(false);
  private readonly figura = viewChild.required<ElementRef<HTMLElement>>('figura');
  private readonly mapa = viewChild<ElementRef<HTMLElement>>('mapa');
  /**
   * Ancho en px de la figura mientras se toma la imagen, o null fuera de la captura. Solo se fija cuando el mapa
   * de calor no cabe y se desplaza de lado: crece lo que le falta al mapa y lo demás queda como en pantalla. Las
   * de lienzo nunca cambian de ancho, porque chart.js las volvería a dibujar.
   */
  public readonly anchoParaImagen = signal<number | null>(null);

  /** Progreso: el porcentaje real, que se escribe junto a la cifra (RN8); su color sale del semáforo por clase CSS. */
  public readonly porcentajeProgreso = computed(() => {
    const vista = this.vista();

    return vista.tipo === 'progreso' ? Math.round((vista.valor / vista.total) * 100) : 0;
  });
  /** Ancho de la barra: el porcentaje con tope en 100, por si el valor pasa del total. */
  public readonly anchoProgreso = computed(() => Math.min(100, this.porcentajeProgreso()));

  /** Mapa de calor: cada celda con su cifra y su color respecto al valor mayor. */
  public readonly celdasMapaCalor = computed<ICeldaMapaCalorSuperEjecutivo[][]>(() => {
    const vista = this.vista();

    if (vista.tipo !== 'mapaCalor') {
      return [];
    }

    const maximo = Math.max(...vista.valores.flat());

    return vista.valores.map((fila) =>
      fila.map((valor) => ({ texto: formatearCifraSuperEjecutivo(valor), color: colorIntensidadSuperEjecutivo(valor, maximo) })),
    );
  });

  public formatear(valor: number, unidad?: string): string {
    return formatearCifraSuperEjecutivo(valor, unidad);
  }

  /** Descarga la gráfica como PNG, tal como se ve, con la fecha de hoy en el nombre. */
  public async descargarImagen(): Promise<void> {
    const vista = this.vista();
    const figura = this.figura().nativeElement;
    const mapa = this.mapa()?.nativeElement;
    const faltante = mapa ? mapa.scrollWidth - mapa.clientWidth : 0;

    await this.descargar(async () => {
      try {
        if (faltante > 0) {
          this.anchoParaImagen.set(figura.offsetWidth + faltante);
          // Un cuadro para que el mapa ya esté dibujado a su ancho completo.
          await new Promise((resolver) => requestAnimationFrame(resolver));
        }

        await descargarImagenSuperEjecutivo(figura, nombreArchivoGraficaSuperEjecutivo(vista.titulo, new Date(), 'png'));
      } finally {
        this.anchoParaImagen.set(null);
      }
    });
  }

  /** Descarga las cifras de la gráfica en Excel, con la fecha de hoy en el nombre. */
  public async descargarExcel(): Promise<void> {
    const vista = this.vista();

    await this.descargar(() =>
      descargarExcelSuperEjecutivo(
        tablaExcelGraficaSuperEjecutivo(vista),
        nombreArchivoGraficaSuperEjecutivo(vista.titulo, new Date(), 'xlsx'),
        EXCEL_SUPER_EJECUTIVO.hojaGrafica,
      ),
    );
  }

  private async descargar(armarArchivo: () => Promise<void>): Promise<void> {
    this.descargando.set(true);

    try {
      await this.descargas.descargar(armarArchivo);
    } finally {
      this.descargando.set(false);
    }
  }
}
